"use client";

import React, { forwardRef } from "react";
import type { FieldError } from "react-hook-form";

type BaseProps = {
  label?: string;
  errors?: FieldError;
  requiredMark?: boolean;
  labelClassName?: string;
};

type InputProps = BaseProps & {
  as?: "input";
} & React.InputHTMLAttributes<HTMLInputElement>;

type TextareaProps = BaseProps & {
  as: "textarea";
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export type SimpleTextFieldProps = InputProps | TextareaProps;

const SimpleTextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  SimpleTextFieldProps
>((props, ref) => {
  const {
    label,
    errors,
    requiredMark,
    labelClassName,
    as = "input",
    className,
    ...rest
  } = props;

  const baseClass =
    "w-full px-3 py-2.5 rounded-lg bg-[#F2F4F7] text-[#1E1D39] " +
    "placeholder:text-neutral-400 border border-neutral-300 " +
    "focus:outline-none transition-all duration-200 " +
    "disabled:opacity-60 disabled:cursor-not-allowed";

  const errorClass = errors
    ? "border-red-500 focus:ring-red-500"
    : "focus:ring-1 focus:ring-[#444CE7]";

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && (
        <label
          className={`text-sm font-medium ${labelClassName ?? "text-[#1E1D39]"}`}
        >
          {label}
          {requiredMark && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {as === "textarea" ? (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          className={`${baseClass} ${errorClass} ${className ?? ""}`}
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          className={`${baseClass} ${errorClass} ${className ?? ""}`}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
        />
      )}

      {errors?.message && (
        <span className="text-xs text-red-600 mt-1">{errors.message}</span>
      )}
    </div>
  );
});

SimpleTextField.displayName = "SimpleTextField";

export default SimpleTextField;
